import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import { FeatureService } from '../../services/features/FeatureService';
import { CoreSessionStorage } from '../../storage/session/CoreSessionStorage';
import { fetchPlayerConfigAction } from '../../store/apiActions/fetchPlayerConfigAction';
import { toastActions } from '../../store/slices/toast/slice';
import { DEFAULT_LANGUAGE_CODE } from '../../utils/language';
import { fetchCasinoConfigAction } from '../apiActions/fetchCasinoConfigAction';
import { fetchPlayerConfigSyncAction } from '../apiActions/fetchPlayerConfigSyncAction';
import { fetchTableConfigAction } from '../apiActions/fetchTableConfigAction';
import type { CoreRootState } from '../CoreStore';
import { subtitlesActions } from '../slices/subtitles/slice';
import { SubtitlesGroup } from '../slices/subtitles/types';

type SyncActionType =
  | typeof subtitlesActions.finishOnboarding.type
  | typeof subtitlesActions.setIsEnabled.type
  | typeof subtitlesActions.setPositions.type
  | typeof subtitlesActions.setSize.type;

export const subtitlesSyncMiddleware = createListenerMiddleware<CoreRootState>();

// Listener to initialize subtitles group
const subtitleGroupInitListenerCancel = subtitlesSyncMiddleware.startListening({
  actionCreator: fetchTableConfigAction.fulfilled,
  effect: (_, { dispatch, getState }) => {
    const {
      config: { tableConfig, session },
      subtitles: { subtitleLanguages },
    } = getState();

    if (!(tableConfig && session)) {
      return;
    }

    const dealerLanguage = (tableConfig.dedicatedLanguage || DEFAULT_LANGUAGE_CODE).toLowerCase();
    const playerLanguage = (session.lang || DEFAULT_LANGUAGE_CODE).toLowerCase();
    const enabledLanguages = subtitleLanguages ?? [];
    let subtitlesGroup: SubtitlesGroup;
    if (dealerLanguage === playerLanguage) {
      subtitlesGroup = SubtitlesGroup.GROUP1;
    } else if (enabledLanguages.includes(playerLanguage)) {
      subtitlesGroup = SubtitlesGroup.GROUP2;
    } else {
      subtitlesGroup = SubtitlesGroup.GROUP3;
    }

    dispatch(subtitlesActions.setGroup(subtitlesGroup));
    subtitleGroupInitListenerCancel();
  },
});

subtitlesSyncMiddleware.startListening({
  matcher: isAnyOf(fetchTableConfigAction.fulfilled, fetchCasinoConfigAction.fulfilled),
  effect: (_, { dispatch, getState }) => {
    const {
      config: { tableConfig, casinoConfig },
    } = getState();
    if (tableConfig && casinoConfig) {
      const isSubtitlesAvailable = tableConfig.subtitlesAvailable && casinoConfig.subtitlesAvailable;
      dispatch(subtitlesActions.setIsAvailable(isSubtitlesAvailable));
    }
  },
});

subtitlesSyncMiddleware.startListening({
  matcher: isAnyOf(
    // TODO: isAllOf?
    fetchTableConfigAction.fulfilled,
    fetchCasinoConfigAction.fulfilled,
    fetchPlayerConfigAction.fulfilled
  ),
  effect: (_, { getState, dispatch }) => {
    const {
      config: { tableConfig, session, casinoConfig, playerConfig },
      subtitles,
    } = getState();
    if (!playerConfig || !tableConfig || !casinoConfig || !session || !subtitles.isAvailable) return;
    /* Show toast to user of subtitles disabled when user has switched a game */
    const { gametype } = session;
    const valueFromStorage = CoreSessionStorage.getSubtitlesDisabled();

    let expectedFromStorage = gametype;
    if (tableConfig && tableConfig.tableVariant) {
      expectedFromStorage = `${gametype}-${tableConfig.tableVariant}`;
    }

    if (tableConfig && tableConfig.tableType && valueFromStorage) {
      const newSubtitles = FeatureService.getFeature('newSubtitles');
      const isSubtitlesEnabled = subtitles.isEnabled;
      const isOnboarded = subtitles.isOnboarded;
      const showDisabledToast =
        newSubtitles && !isSubtitlesEnabled && isOnboarded && expectedFromStorage !== valueFromStorage;
      if (showDisabledToast) {
        dispatch(toastActions.createToast('TOAST_SUBTITLES_GO_TO_SETTINGS'));
        CoreSessionStorage.setSubtitlesDisabled(expectedFromStorage);
      }
    }
  },
});

// Listener to save subtitles config to the player config when the subtitles config changes
subtitlesSyncMiddleware.startListening({
  // List of actions that trigger this middleware to send POST messages to the server
  matcher: isAnyOf(
    subtitlesActions.finishOnboarding,
    subtitlesActions.setIsEnabled,
    subtitlesActions.setPositions,
    subtitlesActions.setSize
  ),
  effect: (action, { dispatch, getState }) => {
    const state = getState();
    const session = state.config.session;
    const tableVariant = state.config.tableConfig?.tableVariant;

    if (!session) return;

    const { JSESSIONID, metaServer: url, gametype } = session;
    if (!(JSESSIONID && url)) return;

    const body = generateBody(action.type as SyncActionType, state);
    const timeStamp = Date.now();
    const newSubtitles = FeatureService.getFeature('newSubtitles');
    if (newSubtitles) {
      CoreSessionStorage.setSubtitlesDisabled(
        !state.subtitles.isEnabled ? (tableVariant ? `${gametype}-${tableVariant}` : gametype) : ''
      );
    }

    dispatch(
      fetchPlayerConfigSyncAction({
        JSESSIONID,
        ck: timeStamp.toString(),
        url: `${url}/api/ui/savePlayerConfigGeneric`,
        body: { ...body, gameType: gametype ?? '' },
      })
    );
  },
});

const generateBody = (actionType: SyncActionType, state: CoreRootState): Record<string, unknown> => {
  const { subtitles } = state;

  switch (actionType) {
    case subtitlesActions.finishOnboarding.type: {
      return { sub_onboarded: subtitles.isOnboarded };
    }
    case subtitlesActions.setIsEnabled.type: {
      return { sub_enabled: subtitles.isEnabled };
    }
    case subtitlesActions.setPositions.type: {
      return { sub_positions: JSON.stringify(subtitles.positionsRaw) };
    }
    case subtitlesActions.setSize.type: {
      return { sub_sizes: JSON.stringify(subtitles.sizes) };
    }
    default: {
      return {};
    }
  }
};
